import React, { FC } from 'react';
import Image from 'next/image';
import styles from './ClientBanner.module.scss';
import classNames from 'classnames';
import parse from 'html-react-parser';
// Import lib files
import { bannerProps } from '@/libs/interfaceProps/bannerProps';
import { fetcher } from '@/libs/fetcher/fetcher';

// import static assets
import useSWR from 'swr';
import Skeleton from './ClientBannerSkeleton';
import TruncateReadmore from '@/components/TruncateReadmore/TruncateReadmore';
import { useEnvContext } from '@/context/envContext';
import CustomLogo from '@/components/CustomLogo/CustomLogo';
import { sanitizeTitle } from '@/libs/sanitizeTitle/sanitizeTitle';
import TwoSplitLogo from '@/components/TwoSplitLogo/TwoSplitLogo';
import TwoSplitTaglineLogo from '@/components/TwoSplitLogo/TwoSplitTaglineLogo';
import CustomLogoHorizontal from '@/components/CustomLogoHorizontal/CustomLogoHorizontal';
import CustomSingleLogo from '@/components/CustomSingleLogo/CustomSingleLogo';
import CustomLogoHorizontalTagline from '@/components/CustomLogoHorizontal/CustomLogoHorizontalTagline';
import CustomLogoMobileSize from '@/components/CustomLogoHorizontal/CustomLogoMobileSize';
const CustomBanner: FC<bannerProps> = ({
  orgId,
  lang,
  t,
  customTemplateName
}) => {
  const {
    cdnEnv: env
  } = useEnvContext();
  const {
    data,
    error,
    isLoading
  } = useSWR(`/hub/services/orgBanner?orgId=${orgId}&lang=${lang}`, fetcher);
  if (error) return;
  if (isLoading) return <Skeleton />;

  // Todo Add  orgCoverImage to this const list
  const {
    orgName,
    orgDescription,
    orgNumFound,
    orgUrl
  } = data;
  const resourcesIcon = `${env}/rms/images/icons/resources-icon.svg`;
  const orgIcon = `${env}/rms/images/icons/link-icon.svg`;

  // Build a display name for the client site link
  // function is only called from within the query check for valid orgUrl
  function orgSiteName(url: string) {
    try {
      const orgSiteNameParts = new URL(url);
      // from the parts get the hostname e.g 'www.example.com'
      let orgHostName = orgSiteNameParts.hostname;
      // split the hostname into an array www, example, com
      let orgHostNameParts = orgHostName.replace('www.', '').split('/');
      // rebuild the parts without the 'www' part
      return orgHostNameParts[0];
    } catch (e) {
      // If URL is invalid, it might be missing a protocol.
      let orgHostNameParts = url.replace('https://', '').replace('http://', '').replace('www.', '').split('/');
      return orgHostNameParts[0];
    }
  }
  function renderCustomTemplateLogo(template: string | undefined) {
    switch (template) {
      case 'two-logo-horz':
        return <CustomLogo />;
      case 'two-split-logo-horz':
        return <TwoSplitLogo />;
      case 'two-split-tagline-dell-microsoft':
        return <TwoSplitTaglineLogo />;
      case 'two-split-tagline-logo-horz':
        return <TwoSplitTaglineLogo />;
      case 'two-logo-horz-dell-intel':
        return <CustomLogoHorizontal />;
      case 'two-logo-horz-dell-stack-intel':
        return <CustomLogoHorizontal />;
      case 'two-logo-horz-dell-intel-tagline':
        return <CustomLogoHorizontalTagline />;
      case 'two-logo-horz-mobile-size':
        return <CustomLogoMobileSize />;
      case 'two-logo-dell-stack-xeon':
        return <CustomLogoMobileSize />;
      case 'single-logo-horz':
        return <CustomSingleLogo />;
      default:
        return <CustomLogo />;
    }
  }
  return <div className={'banners client-banner ' + styles['custom-banner-image']} data-bdd={'custom-banner-image'} data-sentry-component="CustomBanner" data-sentry-source-file="CustomBanner.tsx">
   <div className={styles['banner-background-image']}>
    <div className={classNames(styles['the-client'], styles['custom-client-container'], styles['brand-logosx3'], styles['multiple-brand-logos'], styles['without-hero-banner'])}>
     {renderCustomTemplateLogo(customTemplateName)}

     <div className={styles['client-border']}></div>

     <div className={classNames(styles['client-details'], styles['titleLineBreak'])}>
      <h2 className={styles['org-title']} data-bdd={'client-banner-heading'} data-testid={'client-banner-heading'}>
       {parse(sanitizeTitle(orgName))}
      </h2>
      <p className={styles['client-details-bottom']}>
       <span className={styles['resource-count']} data-bdd={'resource-count'}>
        <Image src={resourcesIcon} width={'16'} height={'16'} className={styles['icon-center-text']} alt="Resources Found" data-sentry-element="Image" data-sentry-source-file="CustomBanner.tsx" />
        {orgNumFound + ' ' + t('Resources') + ' '}
       </span>
       <span className={styles['client-link']} data-bdd={'client-link'}>
        {orgUrl ? <>
          {' '}
          <Image src={orgIcon} width={'16'} height={'17'} className={styles['icon-center-text']} alt="Org Link" />
          <a className={'text-links-body-color'} href={orgUrl} target="_blank" rel={'nofollow'}>
           {' '}
           {orgSiteName(orgUrl)}
          </a>
         </> : ''}
       </span>
      </p>
     </div>
    </div>

    <div className={styles['client-banner-strapline']}>
     <TruncateReadmore classToken={'client-description'} numberOfLines={2} trimBy={'words'} text={sanitizeTitle(orgDescription)} data-sentry-element="TruncateReadmore" data-sentry-source-file="CustomBanner.tsx" />
    </div>
   </div>
  </div>;
};
export default CustomBanner;